import { DATE_FORMAT, DATE_FORMAT_SHORT, RoutingType } from '@constants';
import { Prototype } from '@core';
import { ColTypeDef, ValueFormatterParams } from 'ag-grid-community';
import { find } from 'lodash';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import trans from 'translation';

const dateTimeFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return Prototype.date.toLocal(v)?.format(DATE_FORMAT);
};

const dateFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return Prototype.date.toLocal(v)?.format(DATE_FORMAT_SHORT);
};

const optionFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v ? trans(`option.${v.toLowerCase()}`).toUpperCase() : '';
};

const transFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v ? trans(v.toLowerCase()).toUpperCase() : '';
};

const addressFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v?.address ?? '';
};

const addressRoutingFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  if (v?.routingType?.includes(RoutingType.Warehouse)) {
    return v?.relatedPartySite?.relatedSite?.address ?? '';
  }
  return v?.client?.defaultSite?.address ?? v?.address ?? '';
};

const nameFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v?.name ?? '';
};

const codeFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v?.code ?? '';
};

const naFormatter = (p: ValueFormatterParams) => {
  const v = p.value;
  return v || 'N/A';
};

const pinFormatter = (p: ValueFormatterParams) => {
  if (p.data?.fromRoutingType?.includes('CTO')) {
    return p.data?.jobContainerRoutingStart?.pin || '';
  } else if (p.data?.endRoutingType?.includes('CTO')) {
    return p.data?.jobContainerRoutingEnd?.pin || '';
  } else if (p.data?.jobContainerRoutingStart?.routingType?.includes('CTO')) {
    return p.data?.jobContainerRoutingStart?.pin || '';
  } else if (p.data?.jobContainerRoutingEnd?.routingType?.includes('CTO')) {
    return p.data?.jobContainerRoutingEnd?.pin || '';
  }
  return '';
};

export const useColTypes = <T>() => {
  return useMemo(() => {
    return {
      vnDate: {
        valueFormatter: dateFormatter
      },
      vnDateTime: {
        valueFormatter: dateTimeFormatter
      },
      trans: {
        valueFormatter: transFormatter
      },
      address: {
        valueFormatter: addressFormatter
      },
      code: {
        valueFormatter: codeFormatter
      },
      pin: {
        valueFormatter: pinFormatter
      },
      name: {
        valueFormatter: nameFormatter
      },
      addressRouting: {
        valueFormatter: addressRoutingFormatter
      },
      na: {
        valueFormatter: naFormatter
      }
    } as { [key: string]: ColTypeDef<T> };
  }, []);
};

export const getRoutingType = (rt: RoutingType): RoutingType => {
  return (
    find(
      [
        RoutingType.Cto,
        RoutingType.EmptyYard,
        RoutingType.RailHead,
        RoutingType.WaitDepot,
        RoutingType.Warehouse
      ],
      s => rt?.includes(s)
    ) || rt
  );
};

export const getDisabledState = (data: any) => {
  if (data?.routingStatus === 'COMPLETED') {
    return true;
  }

  if (
    data?.routingStatus === 'SCHEDULED' ||
    data?.routingStatus === 'IN_TRANSIT'
  ) {
    const hasValidScheduleDate =
      !!data?.scheduleDate &&
      moment(data.scheduleDate).diff(moment(), 'day') >= 0;

    return !hasValidScheduleDate || data?.isLocked;
  }

  return false;
};

// Utility to build next/prev editable cell input selector
export const getNextInputSelector = (
  rowIndex: number,
  currentColId: string,
  direction: -1 | 1
) => {
  const allCells = Array.from(
    document.querySelectorAll(
      `[row-index="${rowIndex}"] .ag-cell-inline-editing`
    )
  );

  const currentIndex = allCells.findIndex(
    cell => cell.getAttribute('col-id') === currentColId
  );

  let nextIndex = currentIndex + direction;
  if (nextIndex > allCells.length - 1) {
    nextIndex = 0;
  } else if (nextIndex < 0) {
    nextIndex = allCells.length - 1;
  }

  if (nextIndex >= 0) {
    for (let i = nextIndex; i >= 0 && i < allCells.length; i += direction) {
      const cell = allCells[i];
      const input = cell.querySelector('input');
      if (input && !input?.disabled) {
        input.focus();
        input.select?.();
        return input;
      }
    }
  }

  return '';
};
